import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { CoverageNetwork } from '../../../shared/models/coverage.model';
import { BasicUser, Role } from '../../../shared/models/user.model';
import { BrowserUtils } from '../../../shared/services/browser-utils';
import { CoverageService } from '../../../shared/services/coverage.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UsersService } from '../../../shared/services/users.service';
import { UsersSelectComponent } from '../../../users/users-select/users-select.component';
import { CoverageTableComponent } from '../coverage-table/coverage-table.component';

@Component({
  standalone: false,
  selector: 'app-coverage-index',
  templateUrl: './coverage-index.component.html',
  styleUrls: ['coverage-index.component.scss'],
})
export class CoverageIndexComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(CoverageTableComponent, { static: false }) table: CoverageTableComponent;
  @ViewChild(UsersSelectComponent) mainaccountSelect: UsersSelectComponent;

  @ViewChild('tableWrapper', { static: false }) tableWrapper: ElementRef<HTMLElement>;
  @ViewChild('headerWrapper', { static: false }) headerWrapper: ElementRef<HTMLElement>;

  @ViewChild('coverageMapModalTpl', { read: TemplateRef, static: false }) coverageMapModalTpl: TemplateRef<{ dialogRef: DialogRef }>;

  loading = false;
  mapLoading = false;
  isAdmin = false;
  mainaccounts: BasicUser[] = [];

  searchControl = new FormControl();
  private searchSubscription: Subscription;
  private networksSubscription: Subscription;
  private mapSubscription: Subscription;

  networks: CoverageNetwork[] = [];
  mapNetworks: CoverageNetwork[] = [];

  constructor(
    private notificationService: NotificationService,
    private userService: UsersService,
    private service: CoverageService,
    private modal: ModalService,
    title: Title
  ) {
    title.setTitle('Coverage');
  }

  ngOnInit() {
    this.searchSubscription = this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(searchValue => {
      this.table?.search((searchValue || '').trim());
    });
    this.loadNetworks();
    this.userService.getAuthUser().then(u => {
      this.isAdmin = u.role === Role.ADMIN;
    });
  }

  onSelectMainaccount(user: BasicUser) {
    if (!this.isAdmin || this.mainaccounts.some(account => account.id === user.id)) {
      return;
    }
    this.mainaccounts = [...this.mainaccounts, user];
    this.loadNetworks();
  }

  onUnselectMainaccount(user: BasicUser) {
    if (!this.isAdmin || !this.mainaccounts.some(account => account.id === user.id)) {
      return;
    }
    this.mainaccounts = this.mainaccounts.filter(account => account.id !== user.id);
    this.loadNetworks();
  }

  get clientFilterTitle(): string {
    return this.mainaccounts.map(user => user.email).join(', ') || 'Filter by main account';
  }

  onClearMainaccount() {
    if (!this.isAdmin) {
      return;
    }
    this.mainaccountSelect?.resetAll();
    this.mainaccounts = [];
    this.loadNetworks();
  }

  private loadNetworks() {
    this.networksSubscription?.unsubscribe();
    this.loading = true;
    this.networksSubscription = this.service.networks(this.mainaccounts.map(user => user.id)).subscribe({
      next: networks => {
        this.networks = networks;
        this.loading = false;
      },
      error: e => {
        this.networks = [];
        this.notificationService.error({
          title: 'Coverage',
          message: 'An error occurred while loading coverage',
          requestMessage: e.statusText,
          requestCode: e.status,
        });
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.networksSubscription?.unsubscribe();
    this.mapSubscription?.unsubscribe();
  }

  ngAfterViewInit() {
    this.onResize();
  }

  onClickMap(): void {
    if (this.mapLoading) {
      return;
    }
    if (!this.mainaccounts.length && !this.loading) {
      this.mapNetworks = this.networks;
      this.openMap();
      return;
    }
    this.mapLoading = true;
    this.mapSubscription = this.service.networks().subscribe({
      next: networks => {
        this.mapNetworks = networks;
        this.mapLoading = false;
        this.openMap();
      },
      error: e => {
        this.mapLoading = false;
        this.notificationService.error({
          title: 'Coverage',
          message: 'An error occurred while loading countries',
          requestMessage: e.statusText,
          requestCode: e.status,
        });
      },
    });
  }

  private openMap(): void {
    this.modal.alert().dialogClass('modal-dialog max-large-modal').component(this.coverageMapModalTpl).open();
  }

  @HostListener('window:resize')
  onResize() {
    let height = this.calcHeight();
    if (height < 600) {
      height = 600;
    }
    this.tableWrapper.nativeElement.style.height = `${height}px`;
  }

  private calcHeight() {
    const wrapperHeight = BrowserUtils.getPageWrapperHeight();
    if (!wrapperHeight) {
      return 0;
    }
    return wrapperHeight - this.headerWrapper.nativeElement.clientHeight - 140;
  }
}
